/**
 * Pure display-string helpers shared by Countdown, Running, Queue and
 * Finished. No clock access, no engine re-implementation — callers pass in
 * numbers already computed by src/engine (elapsedFor/remainingFor/etc).
 *
 * BUILD-SPEC.md §7 accessibility rule: colour never carries meaning alone,
 * so every numeric render here has a matching text-label helper.
 */

/** `m:ss` for a non-negative duration, `+m:ss` once `remainingSeconds` goes
 * negative (overtime). Never clamps — overtime counts up forever. */
export function formatCountdown(remainingSeconds: number): string {
  const over = remainingSeconds < 0;
  const abs = Math.round(Math.abs(remainingSeconds));
  const mmss = formatMmSs(abs);
  return over ? `+${mmss}` : mmss;
}

/** Plain `m:ss` for an always-non-negative duration (elapsed totals,
 * budgets) — no sign, since these never go negative. */
export function formatDuration(totalSeconds: number): string {
  return formatMmSs(Math.round(Math.max(0, totalSeconds)));
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Text label pairing the countdown number, per the accessibility rule that
 * colour alone must never carry meaning: "30s left" / "Over by 0:20". */
export function describeRemaining(remainingSeconds: number): string {
  if (remainingSeconds < 0) {
    return `Over by ${formatMmSs(Math.round(Math.abs(remainingSeconds)))}`;
  }
  if (remainingSeconds < 60) {
    return `${Math.round(remainingSeconds)}s left`;
  }
  return `${formatMmSs(Math.round(remainingSeconds))} left`;
}
